function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import useStyles from 'isomorphic-style-loader/useStyles';
import keys from 'lodash/keys';
import valuesIn from 'lodash/valuesIn';
import find from 'lodash/find';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import cn from 'classnames';
import { AppContext } from 'context';
import Link from 'components/Link';
import BackToIntegrationApp from 'components/BackToIntegrationApp';
import App, { AlertsBus } from 'modules/App';
import { useAction } from 'hooks';
import AccountValidator from '../../AccountValidator';
import { APPS, SIGN_UP_FORM, SCOPE_NAMES } from '../../constants';
import * as selectors from '../../selectors';
import messages from '../../messages';
import InvitationInfo from '../InvitationInfo';
import ConfirmEmail from '../ConfirmEmail';
import ChooseScope from './ChooseScope';
import { getStepValidationRules } from './helpers';
import { useSignUpSteps, useSignUpEffects } from './hooks';
import UserInfo from './UserInfo';
import FillData from './FillData';
import styles from './SignUp.pcss';
const getBlsConstants = (blsKey)=>find(valuesIn(APPS), (app)=>app.key === blsKey);
const SignUpContent = ({ children, signInUrl, returnUrl })=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BackToIntegrationApp, {
                to: returnUrl
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.root, 'pageContentBox'),
                children: [
                    /*#__PURE__*/ _jsx("header", {
                        className: "pageHeader",
                        children: /*#__PURE__*/ _jsx("h1", {
                            className: "text--h1 align-items-center text--medium",
                            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.headers.signUp))
                        })
                    }),
                    /*#__PURE__*/ _jsx(InvitationInfo, {
                        className: "mb-6"
                    }),
                    /*#__PURE__*/ _jsx(AlertsBus, {
                        className: "mb-6"
                    }),
                    children
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text--normal text-align--center mt-7",
                children: [
                    /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.infos.haveAccount)),
                    " ",
                    ' ',
                    /*#__PURE__*/ _jsx(Link, {
                        to: signInUrl,
                        className: "text--primary",
                        children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.buttons.signIn))
                    })
                ]
            })
        ]
    });
const SignUp = ({ identityRequirements, identityDefaults, blsKey, returnUrl })=>{
    useStyles(styles);
    const context = useContext(AppContext);
    const invitation = useSelector(selectors.invitation);
    const isIntegrationProcess = useSelector(App.selectors.isIntegrationProcess);
    const onSetFormValue = useAction(App.actions.setFormValue, SIGN_UP_FORM);
    const validatorRules = getStepValidationRules('FillData', 'default');
    const onValidatedSetFormValue = App.hooks.useValidatedSetFormValue(SIGN_UP_FORM, validatorRules, {
        stopOnFirstError: false,
        Validator: AccountValidator
    });
    const allowedScopeNames = useMemo(()=>{
        var _getBlsConstants;
        if (!isEmpty(invitation)) return invitation.allowedScopes;
        if (has(identityRequirements, 'scope')) return [
            identityRequirements.scope
        ];
        if (!isEmpty(blsKey)) return (_getBlsConstants = getBlsConstants(blsKey)) === null || _getBlsConstants === void 0 ? void 0 : _getBlsConstants.scopes;
        return SCOPE_NAMES;
    }, [
        invitation,
        blsKey,
        identityRequirements
    ]);
    const [email, setEmail] = useState();
    const [password, setPassword] = useState();
    const { onContinue, onBack, setConfirmEmail, currentViewName, setDefaultFirstStep } = useSignUpSteps(allowedScopeNames, returnUrl);
    const onSetCredentialFormValue = (input)=>{
        const { id, value } = input;
        if (id === 'email') {
            setEmail(value);
            onSetFormValue(input);
        } else if (id === 'password') {
            setPassword(value);
            onValidatedSetFormValue(input);
        }
    };
    useSignUpEffects(identityRequirements, identityDefaults, returnUrl, setConfirmEmail, setDefaultFirstStep, onSetCredentialFormValue);
    const allowedCountryCodes = useMemo(()=>{
        var _getBlsConstants;
        if (!isEmpty(invitation)) return invitation.allowedCountries;
        const countryCode = get(identityRequirements, 'countryCode');
        if (countryCode) return [
            countryCode
        ];
        if (!isEmpty(blsKey)) return (_getBlsConstants = getBlsConstants(blsKey)) === null || _getBlsConstants === void 0 ? void 0 : _getBlsConstants.countries;
        return undefined;
    }, [
        invitation,
        blsKey,
        identityRequirements
    ]);
    const disabledFields = useMemo(()=>{
        if (invitation) {
            const fields = [
                'countryCode'
            ];
            if (!invitation.organizationUID) fields.push('email');
            return fields;
        }
        if (!isEmpty(identityRequirements)) {
            return keys(identityRequirements);
        }
        return [];
    }, [
        identityRequirements,
        invitation
    ]);
    const isFieldDisabled = (fieldName)=>includes(disabledFields, fieldName);
    const signInUrl = useMemo(()=>{
        if (!returnUrl) return context.getUrl('sign-in');
        return isIntegrationProcess ? `${context.getUrl('sign-in')}?ReturnUrl=${encodeURIComponent(returnUrl)}` : context.getUrl('sign-in');
    }, [
        returnUrl,
        isIntegrationProcess
    ]);
    if (currentViewName === 'ConfirmEmail') {
        return /*#__PURE__*/ _jsx(ConfirmEmail, {
            email: email,
            password: password,
            returnUrl: returnUrl
        });
    }
    if (!returnUrl) return null;
    let CurrentView = null;
    switch(currentViewName){
        case 'ChooseScope':
            CurrentView = /*#__PURE__*/ _jsx(ChooseScope, {
                availableScopes: allowedScopeNames || SCOPE_NAMES,
                onContinue: onContinue,
                onBack: onBack
            });
            break;
        case 'UserInfo':
            CurrentView = /*#__PURE__*/ _jsx(UserInfo, {
                isFieldDisabled: isFieldDisabled,
                onContinue: onContinue,
                onBack: onBack
            });
            break;
        case 'FillData':
            CurrentView = /*#__PURE__*/ _jsx(FillData, {
                isFieldDisabled: isFieldDisabled,
                allowedCountryCodes: allowedCountryCodes,
                onBack: onBack,
                onSetCredentialFormValue: onSetCredentialFormValue
            });
            break;
        default:
            CurrentView = null;
    }
    return /*#__PURE__*/ _jsx(SignUpContent, {
        signInUrl: signInUrl,
        returnUrl: returnUrl,
        children: CurrentView
    });
};
export default SignUp;
